import React from 'react';
import { RefreshCw, Home, AlertTriangle } from 'lucide-react';

/**
 * ErrorBoundary — Catches unhandled React errors.
 * Without this, ANY uncaught JS error shows a white blank screen.
 * Wrap around the whole app (App.js) and around critical route groups.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // In production, send to error tracking service (Sentry, etc.)
    if (process.env.NODE_ENV !== 'production') {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
    this.setState({ errorInfo });
  }

  handleRefresh = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            {/* Gold decorative line */}
            <div className="w-12 h-px bg-crimson mx-auto mb-8" />

            <AlertTriangle
              size={36}
              className="text-crimson-ink mx-auto mb-5"
              strokeWidth={1}
            />

            <h1 className="font-sans font-extrabold tracking-[-0.01em] text-3xl text-charcoal dark:text-white mb-3">
              Something went wrong
            </h1>
            <p className="text-sm font-mulish text-stone dark:text-white/80 mb-8 leading-relaxed">
              We apologise for the inconvenience. Please try refreshing the page
              or returning to the homepage.
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRefresh}
                className="flex items-center gap-2 bg-crimson dark:bg-crimson text-white dark:text-white px-6 py-3 text-[14px] font-mulish hover:bg-crimson-deep hover:text-white transition-all font-bold rounded-full min-h-[44px]"
              >
                <RefreshCw size={13} /> Refresh Page
              </button>
              <button
                onClick={this.handleHome}
                className="flex items-center gap-2 border border-charcoal dark:border-white text-charcoal dark:text-white px-6 py-3 text-xs font-mulish hover:border-crimson hover:text-crimson-ink transition-all font-bold"
              >
                <Home size={13} /> Go Home
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-xs text-stone cursor-pointer font-mulish">
                  Dev: Error details
                </summary>
                <pre className="mt-2 text-xs text-[#B3261E] bg-red-50 dark:bg-red-950 p-4 overflow-auto max-h-48 font-mono">
                  {this.state.error?.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
